import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Card, CardBody, CardHeader,
  Container, Badge, Button, Table,
} from "reactstrap";
import { adminPaths } from "admin/paths";
import AdminListControls from "components/admin/AdminListControls";
import AdminPageHeader from "components/admin/AdminPageHeader";
import AdminStateBlock from "components/admin/AdminStateBlock";
import { subscriptionsRepo } from "data/repositories/subscriptionsRepo";
import { usersRepo } from "data/repositories/usersRepo";
import { paymentPlanIds, subscriptionStatuses } from "data/admin/domain";
import { confirmAdminAction, sortByOption, toDateLabel } from "admin/contentUtils";

const STATUS_COLOR = { active: "success", cancelled: "danger", expired: "secondary" };
const sortOptions = [
  { value: "updated-desc", label: "Son güncellenen" },
  { value: "started-asc", label: "Başlangıç tarihi" },
  { value: "user-asc", label: "Kullanıcıya göre" },
];

export default function AdminSubscriptions() {
  const location = useLocation();
  const navigate = useNavigate();
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [flash, setFlash] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [planFilter, setPlanFilter] = useState("");
  const [stateFilter, setStateFilter] = useState("active");
  const [sortBy, setSortBy] = useState("updated-desc");

  const userMap = useMemo(() => {
    const map = new Map();
    usersRepo.list({ includeDeleted: true }).forEach((item) => map.set(item.uid || item.id, item));
    return map;
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    setError("");

    try {
      setSubs(subscriptionsRepo.list({ includeDeleted: true }));
    } catch (loadError) {
      setError(loadError?.message || "Abonelikler yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!location.state?.status) return;
    setFlash(location.state.status);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    const next = subs.filter((sub) => {
      if (stateFilter === "active" && sub.deletedAt) return false;
      if (stateFilter === "archived" && !sub.deletedAt) return false;
      if (statusFilter && sub.status !== statusFilter) return false;
      if (planFilter && sub.planId !== planFilter) return false;
      if (!query) return true;

      const user = userMap.get(sub.userId);
      return [sub.id, sub.userId, sub.planId, sub.status, user?.displayName, user?.email].join(" ").toLowerCase().includes(query);
    });

    return sortByOption(next, sortBy, {
      "started-asc": (left, right) => String(left.startedAt || "").localeCompare(String(right.startedAt || "")),
      "user-asc": (left, right) => String(left.userId || "").localeCompare(String(right.userId || ""), "tr"),
    });
  }, [planFilter, search, sortBy, stateFilter, statusFilter, subs, userMap]);

  const handleToggle = (id, currentStatus) => {
    const newStatus = currentStatus === "active" ? "cancelled" : "active";
    try {
      subscriptionsRepo.update(id, { status: newStatus });
      setFlash({ color: "success", message: `Abonelik durumu ${newStatus} olarak güncellendi.` });
      load();
    } catch (updateError) {
      setFlash({ color: "danger", message: updateError?.message || "Abonelik güncellenemedi." });
    }
  };

  const handleRemove = (id) => {
    if (!confirmAdminAction("Bu abonelik")) return;
    try {
      subscriptionsRepo.remove(id);
      setFlash({ color: "success", message: "Abonelik arşive alındı." });
      load();
    } catch (removeError) {
      setFlash({ color: "danger", message: removeError?.message || "Abonelik arşivlenemedi." });
    }
  };

  const handleRestore = (id) => {
    try {
      subscriptionsRepo.restore(id);
      setFlash({ color: "success", message: "Abonelik tekrar yayına alındı." });
      load();
    } catch (restoreError) {
      setFlash({ color: "danger", message: restoreError?.message || "Abonelik geri alınamadı." });
    }
  };

  const resetFilters = () => {
    setSearch("");
    setStatusFilter("");
    setPlanFilter("");
    setStateFilter("active");
    setSortBy("updated-desc");
  };

  return (
    <>
      <AdminPageHeader
        title="Admin — Abonelikler"
        description="Subscriptions modülünde anlamlı operasyonlar activate ve cancel olarak tutulur."
      />

      <Container className="mt--6" fluid>
        <Card className="shadow">
          <CardHeader>
            <h3 className="mb-3">Tüm Abonelikler</h3>
            <AdminListControls
              search={search}
              onSearch={setSearch}
              searchPlaceholder="Kullanıcı, plan veya abonelik ara"
              filters={[
                {
                  key: "status",
                  label: "Durum",
                  value: statusFilter,
                  onChange: setStatusFilter,
                  options: [{ value: "", label: "Tüm durumlar" }, ...subscriptionStatuses.map((status) => ({ value: status, label: status }))],
                },
                {
                  key: "plan",
                  label: "Plan",
                  value: planFilter,
                  onChange: setPlanFilter,
                  options: [{ value: "", label: "Tüm planlar" }, ...paymentPlanIds.map((plan) => ({ value: plan, label: plan }))],
                },
                {
                  key: "state",
                  label: "Kayıt durumu",
                  value: stateFilter,
                  onChange: setStateFilter,
                  options: [
                    { value: "active", label: "Aktif kayıtlar" },
                    { value: "archived", label: "Arşiv" },
                    { value: "all", label: "Tümü" },
                  ],
                },
              ]}
              sortValue={sortBy}
              onSortChange={setSortBy}
              sortOptions={sortOptions}
              onReset={resetFilters}
              resultCount={filtered.length}
              totalCount={subs.length}
            />
          </CardHeader>
          <CardBody>
            <AdminStateBlock
              flash={flash}
              onDismissFlash={() => setFlash(null)}
              error={error}
              onDismissError={() => setError("")}
              loading={loading}
              spinnerColor="dark"
              isEmpty={filtered.length === 0}
              emptyTitle="Henüz abonelik yok"
            >
              <Table className="align-items-center" responsive>
                <thead className="thead-light">
                  <tr>
                    <th>Abonelik</th>
                    <th>Plan</th>
                    <th>Kullanıcı</th>
                    <th>Başlangıç</th>
                    <th>Bitiş</th>
                    <th>Durum</th>
                    <th className="text-right">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((s) => {
                    const user = userMap.get(s.userId);
                    return (
                    <tr key={s.id}>
                      <td>
                        <div className="font-weight-bold">{user?.displayName || s.userId}</div>
                        <div className="small text-muted">{s.id}</div>
                      </td>
                      <td>{s.planId || "-"}</td>
                      <td>
                        <div>{user?.displayName || s.userId || "-"}</div>
                        <div className="small text-muted">{user?.email || s.userId || "-"}</div>
                      </td>
                      <td>{toDateLabel(s.startedAt)}</td>
                      <td>{toDateLabel(s.expiresAt)}</td>
                      <td>
                        <Badge color={STATUS_COLOR[s.status] || "secondary"} pill>
                          {s.status}
                        </Badge>
                      </td>
                      <td className="text-right">
                        <Button color="primary" size="sm" className="mr-2" tag={Link} to={`/admin${adminPaths.admin.subscriptionDetail(s.id)}`}>
                          Detay
                        </Button>
                        <Button
                          color={s.status === "active" ? "warning" : "success"}
                          size="sm"
                          className="mr-1"
                          onClick={() => handleToggle(s.id, s.status)}
                        >
                          {s.status === "active" ? "İptal Et" : "Aktifleştir"}
                        </Button>
                        {s.deletedAt ? (
                          <Button color="success" outline size="sm" onClick={() => handleRestore(s.id)}>
                            Geri al
                          </Button>
                        ) : (
                          <Button color="danger" size="sm" onClick={() => handleRemove(s.id)}>
                            Arşivle
                          </Button>
                        )}
                      </td>
                    </tr>
                  )})}
                </tbody>
              </Table>
            </AdminStateBlock>
          </CardBody>
        </Card>
      </Container>
    </>
  );
}
